import { useMemo } from "react";

import {
  useGetCashflowSeriesQuery,
  useGetGroupedTransactionsQuery,
} from "../../../services";
import { CashflowSeriesPoint } from "../../../services/transactions";
import { colorForCategory, TransactionType } from "../../../utils";
import { INSIGHTS_TOP_CATEGORIES } from "../components/Insights/constants";
import {
  CategoryBreakdownPoint,
  InsightsWindow,
} from "../components/Insights/types";
import { getInsightsWindow } from "../components/Insights/utils";
import { FilterValues } from "../components/TransactionsFilters/types";
import { PeriodState } from "../TransactionsPage/types";

interface UseInsightsDataProps {
  period: PeriodState;
  filters: FilterValues;
  debouncedSearch: string;
  isCompact: boolean;
  enabled: boolean; // false when Type=Transfer
}

export interface UseInsightsDataReturn {
  window: InsightsWindow;
  series: CashflowSeriesPoint[];
  categories: CategoryBreakdownPoint[];
  isLoading: boolean;
  hasData: boolean;
}

export const useInsightsData = ({
  period,
  filters,
  debouncedSearch,
  isCompact,
  enabled,
}: UseInsightsDataProps): UseInsightsDataReturn => {
  const window = useMemo(
    () => getInsightsWindow(period, isCompact),
    [period, isCompact],
  );

  const categoryId = filters.category
    ? Number(filters.category.value)
    : undefined;
  const accountId = filters.account ? Number(filters.account.value) : undefined;
  const typeValue = filters.type.value as TransactionType;

  const seriesQuery = useGetCashflowSeriesQuery(
    {
      bucket: window.bucket,
      from: window.from,
      to: window.to,
      type: [typeValue],
      categoryId,
      searchBy: debouncedSearch,
      accountId,
    },
    { skip: !enabled },
  );

  // The category breakdown always defaults to expenses when no type is selected.
  // Loan payments are folded into expenses by the API.
  const breakdownTypes =
    typeValue === TransactionType.ALL ? [TransactionType.EXPENSE] : [typeValue];

  const breakdownQuery = useGetGroupedTransactionsQuery(
    {
      groupBy: categoryId === undefined ? "category" : "subcategory",
      type: breakdownTypes,
      searchBy: debouncedSearch,
      dateRange: period.dateRange,
      year: period.dateRange === "all" ? undefined : period.year,
      month: period.dateRange === "month" ? period.month : undefined,
      categoryId,
      accountId,
      limit: INSIGHTS_TOP_CATEGORIES,
    },
    { skip: !enabled },
  );

  const series = useMemo<CashflowSeriesPoint[]>(
    () => (enabled ? (seriesQuery.data ?? []) : []),
    [enabled, seriesQuery.data],
  );

  const categories = useMemo<CategoryBreakdownPoint[]>(() => {
    if (!enabled) {
      return [];
    }
    return (breakdownQuery.data ?? []).map((entry) => ({
      label: entry.name,
      value: Math.abs(entry.totalAmount),
      color: colorForCategory(entry.name),
    }));
  }, [enabled, breakdownQuery.data]);

  const isLoading =
    enabled && (seriesQuery.isFetching || breakdownQuery.isFetching);

  const seriesHasValues = series.some(
    (point) => point.income !== 0 || point.expense !== 0,
  );
  const hasData = enabled && seriesHasValues;

  return { window, series, categories, isLoading, hasData };
};
